/**
 * The marketing dashboard's arithmetic (27 Oct 2026). Pure, so the numbers on
 * screen are tested: periods, totals over ad days, and which ads did well.
 *
 * A "result" is a conversation started (a DM or WhatsApp) or a lead: the
 * things that can become an enquiry. Cost per result is what an ad is judged
 * on; clicks and reach only explain it.
 */
import { shiftDays, type IsoDate } from './dates'

export const PERIODS = ['last7', 'last30', 'month', 'last_month', 'quarter', 'last3m', 'year', 'all'] as const
export type Period = (typeof PERIODS)[number]

export const PERIOD_LABEL: Record<Period, string> = {
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  month: 'This month',
  last_month: 'Last month',
  quarter: 'This quarter',
  last3m: 'Last 3 months',
  year: 'This year',
  all: 'All time',
}

/** First and last day of a period, both inclusive. `all` starts in 2020. */
export function periodRange(period: Period, onDate: IsoDate): { from: IsoDate; to: IsoDate } {
  const year = Number(onDate.slice(0, 4))
  const month = Number(onDate.slice(5, 7))
  const first = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}-01`
  switch (period) {
    case 'last7':
      return { from: shiftDays(onDate, -6), to: onDate }
    case 'last30':
      return { from: shiftDays(onDate, -29), to: onDate }
    case 'month':
      return { from: first(year, month), to: onDate }
    case 'last_month': {
      const y = month === 1 ? year - 1 : year
      const m = month === 1 ? 12 : month - 1
      return { from: first(y, m), to: shiftDays(first(year, month), -1) }
    }
    case 'quarter':
      return { from: first(year, Math.floor((month - 1) / 3) * 3 + 1), to: onDate }
    case 'last3m':
      return { from: shiftDays(onDate, -89), to: onDate }
    case 'year':
      return { from: `${year}-01-01`, to: onDate }
    case 'all':
      return { from: '2020-01-01', to: onDate }
  }
}

/** Days in a range, inclusive. */
export function daysIn(from: IsoDate, to: IsoDate): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000) + 1
}

export interface AdDay {
  ad_id: string
  day: string
  spend: number
  impressions: number
  reach: number
  clicks: number
  link_clicks: number
  engagement: number
  messages: number
  leads: number
}

export interface Totals {
  spend: number
  impressions: number
  reach: number
  link_clicks: number
  engagement: number
  results: number
  /** Days with any spend. */
  days: number
}

export function totals(days: AdDay[]): Totals {
  const spent = new Set<string>()
  const sum: Totals = { spend: 0, impressions: 0, reach: 0, link_clicks: 0, engagement: 0, results: 0, days: 0 }
  for (const row of days) {
    sum.spend += Number(row.spend)
    sum.impressions += row.impressions
    sum.reach += row.reach
    sum.link_clicks += row.link_clicks
    sum.engagement += row.engagement
    sum.results += row.messages + row.leads
    if (Number(row.spend) > 0) spent.add(row.day)
  }
  sum.spend = Math.round(sum.spend * 100) / 100
  sum.days = spent.size
  return sum
}

/** Spend per day across the range, zero-filled, for the chart. */
export function spendByDay(days: AdDay[], from: IsoDate, to: IsoDate): Array<{ day: IsoDate; spend: number; results: number }> {
  const byDay = new Map<string, { spend: number; results: number }>()
  for (const row of days) {
    const entry = byDay.get(row.day) ?? { spend: 0, results: 0 }
    entry.spend += Number(row.spend)
    entry.results += row.messages + row.leads
    byDay.set(row.day, entry)
  }
  const out: Array<{ day: IsoDate; spend: number; results: number }> = []
  for (let day = from; day <= to; day = shiftDays(day, 1)) {
    const entry = byDay.get(day)
    out.push({ day, spend: Math.round((entry?.spend ?? 0) * 100) / 100, results: entry?.results ?? 0 })
  }
  return out
}

export interface AdSummary extends Totals {
  ad_id: string
  /** Rand per result; null with no results. */
  costPerResult: number | null
  /** Link clicks per impression. */
  ctr: number | null
}

/**
 * Each ad's totals, best first: the lowest cost per result among ads with
 * results, then ads with spend but no results (most spent first, the ones to
 * look at), then ads that didn't run.
 */
export function rankAds(days: AdDay[]): AdSummary[] {
  const byAd = new Map<string, AdDay[]>()
  for (const row of days) byAd.set(row.ad_id, [...(byAd.get(row.ad_id) ?? []), row])
  const summaries = [...byAd.entries()].map(([ad_id, rows]) => {
    const sum = totals(rows)
    return {
      ...sum,
      ad_id,
      costPerResult: sum.results > 0 ? sum.spend / sum.results : null,
      ctr: sum.impressions > 0 ? sum.link_clicks / sum.impressions : null,
    }
  })
  return summaries.sort((a, b) => {
    if (a.costPerResult !== null && b.costPerResult !== null) return a.costPerResult - b.costPerResult
    if (a.costPerResult !== null) return -1
    if (b.costPerResult !== null) return 1
    return b.spend - a.spend
  })
}

/** Rand divided by a count, or null when there is nothing to divide by. */
export function costPer(spend: number, count: number): number | null {
  return count > 0 ? spend / count : null
}
